'use client'

import { useIsMobile } from '@repo/ui/hooks'
import * as React from 'react'

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './drawer'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './sheet'
import { cn } from '@repo/ui/lib/utils'

interface ResponsiveSheetBodyProps extends React.ComponentProps<'div'> {}

interface ResponsiveSheetContentProps {
  children: React.ReactNode
  className?: string
  initialFocus?: boolean | React.RefObject<HTMLElement | null>
  side?: 'bottom' | 'left' | 'right' | 'top'
}

type ResponsiveSheetPresentation = 'default' | 'nested-dialog'

interface ResponsiveSheetProps {
  children: React.ReactNode
  onOpenChange?: (_open: boolean) => void
  open?: boolean
  presentation?: ResponsiveSheetPresentation
}

// `isDrawer` is false for a nested-dialog sheet: it renders as a Sheet on mobile too.
const ResponsiveSheetContext = React.createContext({
  isDrawer: false,
  isMobile: false,
  isNested: false,
})

function ResponsiveSheet({ children, presentation = 'default', ...props }: ResponsiveSheetProps) {
  const isMobile = useIsMobile()
  const isNested = presentation === 'nested-dialog'
  const isDrawer = isMobile && !isNested
  const Comp = isDrawer ? Drawer : Sheet

  return (
    <ResponsiveSheetContext.Provider value={{ isDrawer, isMobile, isNested }} data-sentry-element="ResponsiveSheetContext.Provider" data-sentry-component="ResponsiveSheet" data-sentry-source-file="responsive-sheet.tsx">
      <Comp {...props} data-sentry-element="Comp" data-sentry-source-file="responsive-sheet.tsx">{children}</Comp>
    </ResponsiveSheetContext.Provider>
  )
}

function ResponsiveSheetBody({ className, ...props }: ResponsiveSheetBodyProps) {
  return (
    <div
      className={cn('min-h-0 flex-1 scrollbar-none overflow-y-auto p-px', className)}
      {...props}
    data-sentry-component="ResponsiveSheetBody" data-sentry-source-file="responsive-sheet.tsx" />
  )
}

function ResponsiveSheetClose(props: React.ComponentProps<typeof DrawerClose>) {
  const { isDrawer } = React.useContext(ResponsiveSheetContext)
  if (isDrawer) {
    return <DrawerClose {...props} />
  }
  return <SheetClose {...props} data-sentry-element="SheetClose" data-sentry-component="ResponsiveSheetClose" data-sentry-source-file="responsive-sheet.tsx" />
}

function ResponsiveSheetContent({
  children,
  className,
  initialFocus,
  side = 'right',
  ...props
}: ResponsiveSheetContentProps) {
  const { isDrawer, isMobile, isNested } = React.useContext(ResponsiveSheetContext)

  if (isDrawer) {
    return (
      <DrawerContent {...props}>
        {/* Popups portal into DrawerContent, so clipping and scrolling live on this wrapper. */}
        <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', className)}>
          {children}
        </div>
      </DrawerContent>
    )
  }

  return (
    <SheetContent
      className={cn(
        'z-60 flex flex-col overflow-hidden',
        isNested && 'z-80',
        isNested && isMobile && 'max-h-[80vh] rounded-t-xl',
        className,
      )}
      forceOverlay={isNested}
      initialFocus={initialFocus}
      overlayClassName={isNested ? 'z-71' : undefined}
      showCloseButton={false}
      side={isNested && isMobile ? 'bottom' : side}
      {...props}
    data-sentry-element="SheetContent" data-sentry-component="ResponsiveSheetContent" data-sentry-source-file="responsive-sheet.tsx">
      {children}
    </SheetContent>
  )
}

function ResponsiveSheetDescription(props: React.ComponentProps<typeof DrawerDescription>) {
  const { isDrawer } = React.useContext(ResponsiveSheetContext)
  if (isDrawer) {
    return <DrawerDescription {...props} />
  }
  return <SheetDescription {...props} data-sentry-element="SheetDescription" data-sentry-component="ResponsiveSheetDescription" data-sentry-source-file="responsive-sheet.tsx" />
}

function ResponsiveSheetFooter({ ...props }: React.ComponentProps<'div'>) {
  const { isDrawer } = React.useContext(ResponsiveSheetContext)
  const Comp = isDrawer ? DrawerFooter : SheetFooter
  return <Comp {...props} data-sentry-element="Comp" data-sentry-component="ResponsiveSheetFooter" data-sentry-source-file="responsive-sheet.tsx" />
}

function ResponsiveSheetHeader({ ...props }: React.ComponentProps<'div'>) {
  const { isDrawer } = React.useContext(ResponsiveSheetContext)
  const Comp = isDrawer ? DrawerHeader : SheetHeader
  return <Comp {...props} data-sentry-element="Comp" data-sentry-component="ResponsiveSheetHeader" data-sentry-source-file="responsive-sheet.tsx" />
}

function ResponsiveSheetTitle(props: React.ComponentProps<typeof DrawerTitle>) {
  const { isDrawer } = React.useContext(ResponsiveSheetContext)
  if (isDrawer) {
    return <DrawerTitle {...props} />
  }
  return <SheetTitle {...props} data-sentry-element="SheetTitle" data-sentry-component="ResponsiveSheetTitle" data-sentry-source-file="responsive-sheet.tsx" />
}

function ResponsiveSheetTrigger({
  asChild,
  children,
  ...props
}: React.ComponentProps<typeof DrawerTrigger>) {
  const { isDrawer } = React.useContext(ResponsiveSheetContext)
  if (isDrawer) {
    return (
      <DrawerTrigger asChild={asChild} {...props}>
        {children}
      </DrawerTrigger>
    )
  }
  if (asChild && React.isValidElement(children)) {
    return <SheetTrigger {...props} render={children} />
  }
  return <SheetTrigger {...props} data-sentry-element="SheetTrigger" data-sentry-component="ResponsiveSheetTrigger" data-sentry-source-file="responsive-sheet.tsx">{children}</SheetTrigger>
}

export {
  ResponsiveSheet,
  ResponsiveSheetBody,
  ResponsiveSheetClose,
  ResponsiveSheetContent,
  ResponsiveSheetDescription,
  ResponsiveSheetFooter,
  ResponsiveSheetHeader,
  ResponsiveSheetTitle,
  ResponsiveSheetTrigger,
}
export type { ResponsiveSheetPresentation }
